/**
 * How old the feeds behind a county are, said wherever the county is.
 *
 * A frozen register passes every other check this product makes. It answers
 * the query, publishes the columns we ask for, pages correctly and returns
 * rows — so nothing in the product could tell a dead register from a quiet
 * city. Measured on 31 Aug 2026: **Fort Lauderdale's code register stopped
 * being written to on 3 Oct 2019 and was still serving 4,239 cases marked
 * open**, Clearwater's stopped in 2022 and Daytona Beach's in 2023, and those
 * are the only case registers in Broward, Pinellas and Volusia. All three were
 * withdrawn from scoring on 7 Sep 2026; the wording here still has work to do,
 * because a permit register or a roll can stop being written to as well.
 *
 * The report has printed these dates since the day reports shipped. The
 * screens had not, and the screens are where somebody decides whether a
 * county is worth paying for — so this module is the same statement in the
 * same words, for the dashboard and the coverage map.
 *
 * Three rules hold the wording together, and each is pinned by a test.
 *
 * **It says when the county last wrote, never whether the data is right.**
 * "Not written to since October 2019" is a fact about the register. "Out of
 * date" is a judgement about the properties in it, and we have no grounds for
 * it: a case open in 2019 may well still be open.
 *
 * **A feed that could not be dated is unmeasured, never current.** Some layers
 * publish no date column, some refuse to be sorted, and one answers with a
 * placeholder in 2026-12-31. Read as an age, exactly those would certify a
 * dead feed as the freshest thing in the county.
 *
 * **Silence is not a clean bill of health.** Most counties publish no date
 * column anywhere, so an empty list means we cannot say how current the
 * county is — not that it is current. Nothing here invents a reassurance for
 * that case; the caller renders nothing at all.
 */

import type { SourceAge } from "../api/types";

/** Feeds whose newest record is older than the threshold set for that layer. */
export function staleSources(sources: SourceAge[]): SourceAge[] {
  return sources.filter((source) => source.stale);
}

/** Feeds that could not be dated at all — unmeasured, not current. */
export function undatedSources(sources: SourceAge[]): SourceAge[] {
  return sources.filter((source) => source.newest === null);
}

/** Whether anything here is worth putting in front of a reader. */
export function hasSourceWarning(sources: SourceAge[]): boolean {
  return staleSources(sources).length > 0 || undatedSources(sources).length > 0;
}

/** "3 October 2019", or "" for a date we do not have. */
export function whenWritten(newest: string | null): string {
  if (!newest) return "";
  // Parsed by hand rather than with `new Date("2026-09-03")`, which reads a
  // bare date as UTC midnight and renders it a day earlier for every reader
  // west of Greenwich. `epoch_to_date`'s warning, on a value with no time.
  const [year, month, day] = newest.split("-").map(Number);
  if (!year || !month || !day) return "";
  const at = new Date(year, month - 1, day);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** "6 years", "8 months", "12 days" — the age, in the coarsest honest unit. */
export function howOld(ageDays: number | null): string {
  if (ageDays === null || ageDays < 0) return "";
  if (ageDays < 45) return `${ageDays} ${ageDays === 1 ? "day" : "days"}`;
  const months = Math.round(ageDays / 30);
  if (ageDays < 550) return `${months} months`;
  const years = (ageDays / 365).toFixed(1).replace(/\.0$/, "");
  return `${years} years`;
}

/**
 * One feed, as a line under a county.
 *
 * The label is the county's own name for the feed, so a reader can tell which
 * of two registers is the stale one.
 */
export function sourceLine(source: SourceAge): string {
  if (source.newest === null) {
    // No reason given. The engine's is a raw error — Charlotte's, on 28 Sep
    // 2026, was a service URL and "Expecting value: line 3 column 1" printed
    // under the panel — and the API no longer serves it. The report's rows
    // use these words too (report_render.UNDATED).
    return `${source.label}: not dated — the date could not be read when last checked`;
  }
  const age = howOld(source.age_days);
  const ago = age ? `, ${age} ago` : "";
  return `${source.label}: last written to ${whenWritten(source.newest)}${ago}`;
}

/**
 * The sentence a county gets when one of its feeds has stopped moving.
 *
 * Empty when there is nothing to say, so a caller can render on truthiness
 * without deciding what "nothing" looks like.
 */
export function sourceWarning(sources: SourceAge[]): string {
  const stale = staleSources(sources);
  const undated = undatedSources(sources);
  if (stale.length === 0 && undated.length === 0) return "";

  if (stale.length > 0) {
    const oldest = stale.reduce((worst, source) =>
      (source.age_days ?? 0) > (worst.age_days ?? 0) ? source : worst);
    // The others are a separate sentence rather than a clause on the date.
    // "since October 2019 and 1 more" reads as part of the date, which is the
    // one thing in here a reader is going to quote at somebody.
    const others = stale.length - 1;
    const also = others
      ? ` ${others === 1 ? "Another feed here is" : `${others} other feeds here are`}` +
        " stale too."
      : "";
    return (
      `${oldest.label} has not been written to since ` +
      `${whenWritten(oldest.newest)}. Records from it are still served, and ` +
      `are as old as that.${also}`
    );
  }
  const only = undated.length === 1 ? undated[0] : undefined;
  const which = only ? only.label : `${undated.length} feeds`;
  return `${which} could not be dated, so how current it is was not measured.`;
}
